import type { Attribute } from "@cleanbudget/contract/attribute";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";

/** The page's data: one query, which every write replaces with the list it answers. */
export const attributesQuery = () => orpc.attribute.list.queryOptions();

/** Every write of Producten → Kenmerken; product editors (which show the values) are marked stale. */
export function useAttributeWrites() {
	const queryClient = useQueryClient();
	const options = {
		onSuccess: (list: Attribute[]) => {
			queryClient.setQueryData(attributesQuery().queryKey, list);
			void queryClient.invalidateQueries({ queryKey: orpc.product.get.key() });
		},
		onError: (error: unknown) => {
			if (apiErrorTag(error) === "AttributeOrderOutdated") {
				void queryClient.invalidateQueries({ queryKey: attributesQuery().queryKey });
			}
		},
	};
	return {
		create: useMutation(orpc.attribute.create.mutationOptions(options)),
		update: useMutation(orpc.attribute.update.mutationOptions(options)),
		remove: useMutation(orpc.attribute.delete.mutationOptions(options)),
		reorder: useMutation({
			...orpc.attribute.reorder.mutationOptions(options),
			scope: { id: "attribute-order" },
		}),
		addValue: useMutation(orpc.attribute.addValue.mutationOptions(options)),
		updateValue: useMutation(orpc.attribute.updateValue.mutationOptions(options)),
		removeValue: useMutation(orpc.attribute.deleteValue.mutationOptions(options)),
		reorderValues: useMutation({
			...orpc.attribute.reorderValues.mutationOptions(options),
			scope: { id: "attribute-value-order" },
		}),
	};
}

export type AttributeWrites = ReturnType<typeof useAttributeWrites>;

/** A refused attribute write in Dutch, by the error's tag. */
export function useAttributeErrorMessage() {
	const { t } = useTranslation("attribute");
	const { t: tCommon } = useTranslation();
	return (error: unknown): string =>
		match(apiErrorTag(error))
			.with("AttributeNameTaken", () => t("error.nameTaken"))
			.with("AttributeValueNameTaken", () => t("error.valueNameTaken"))
			.with("AttributeNotFound", () => t("error.notFound"))
			.with("AttributeValueNotFound", () => t("error.valueNotFound"))
			.with("AttributeOrderOutdated", () => t("error.orderOutdated"))
			.otherwise(() => tCommon("form.failed"));
}
